import { useContext, useEffect, useRef, useState } from "react";
import AppContext from "../context/AppContext";
import ApiService from "../api/ApiService";
import notification from "../helpers/notification";
import "../css/RecordingOfScores.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import MonthDropdown from "../components/common/MonthDropdown";
import YearDropdown from "../components/common/YearDropdown";
import CalendarComponent from "../components/common/CalendarComponent";
import exifr from "exifr";
import HarinaamBulkUpdate from "../components/common/BulkUpdateComponent";
import { X, Upload, ChevronLeft, ChevronRight } from "lucide-react";
import HarinaamBulkSchedule from "../components/common/BulkScheduleComponent";

// Define points per book type
const POINTS_PER_BOOK = {
  ChaitanyaCharitamritaSet: 36.0,
  SrimadBhagavatamSet: 72.0,
  MahabigBooks: 2.0,
  BigBooks: 1.0,
  MediumBooks: 0.5,
  SmallBooks: 0.25,
  SrilaPrabhupadaLilamrita: 14,
  Magazines: 0.1,
  "1 Year BTG Subscription-1": 5.0,
  "1 Year BTG Subscription-2": 2.5,
};

const INITIAL_COUNTS = Object.fromEntries(
  Object.keys(POINTS_PER_BOOK).map((key) => [key, 0])
);
const INITIAL_POINTS = Object.fromEntries(
  Object.keys(POINTS_PER_BOOK).map((key) => [key, 0])
);

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const RecordingOfScores = () => {
  const { t } = useTranslation();
  const { user } = useContext(AppContext);
  console.log("User's Temple", user);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [showBulkSchedule, setShowBulkSchedule] = useState(false);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState(INITIAL_COUNTS);
  const [sankirtanDate, setSankirtanDate] = useState("");
  const [participantCount, setParticipantCount] = useState(0);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [isOutreachNewLocation, setIsOutreachNewLocation] = useState("");
  const [isMediaUploaded, setIsMediaUploaded] = useState("");
  const [points, setPoints] = useState(INITIAL_POINTS);
  const [selectedEvent, setSelectedEvent] = useState("Regular Update");
  const [userScoreData, setUserScoreData] = useState({});
  const [harinamCount, setHarinaamCount] = useState(0);
  const [isHarinamComplete, setIsHarinamComplete] = useState(false);
  const [harinamCounts, setHarinaamCounts] = useState(0);
  const [location, setLocation] = useState("");
  const [imageLocations, setImageLocations] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [sankirtanDuration, setSankirtanDuration] = useState("");
  const [localPoints, setLocalPoints] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [photoFiles, setPhotoFiles] = useState([]);
  const photoInputRef = useRef(null);
  const [selectedPhotoNames, setSelectedPhotoNames] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    monthNames[new Date().getMonth()]
  );
  const [displayYear, setDisplayYear] = useState(new Date().getFullYear());
  const [displayMonth, setDisplayMonth] = useState(
    monthNames[new Date().getMonth()]
  );
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [resetCalendarTrigger, setResetCalendarTrigger] = useState(false);

  const handleDateTimeSelect = (dateTime) => {
    setSelectedDateTime(dateTime);
    setSelectedMonth(monthNames[dateTime.date.getMonth()]);
    setSelectedYear(dateTime.date.getFullYear());
  };

  const harinaamScheduleSave = async () => {
    if (!selectedDateTime?.date || !selectedDateTime?.time) {
      notification(
        "error",
        t("Date & time cannot be empty, Please select both date and time")
      );
      return;
    }

    // if (!phoneNumber || phoneNumber.length !== 10) {
    //   notification("error", t("Phone Number cannot be empty, Please enter a valid 10-digit phone number"));
    //   return;
    // }

    // if (!location) {
    //   notification(
    //     "error",
    //     t("Location cannot be empty, Please enter location.")
    //   );
    //   return;
    // }

    const { hour, minute, period } = selectedDateTime.time;
    const formattedTime = `${hour}:${minute
      .toString()
      .padStart(2, "0")} ${period}`;

    const data = [
      {
        location: location,
        temple: user.temple_name,
        date: selectedDateTime.date.toISOString(),
        time: formattedTime,
        phone_number: phoneNumber, // <-- Add phone number here
      },
    ];

    try {
      await ApiService.saveSchedule(data);
      notification("success", t("Sankirtan scheduled successfully"));
      setSelectedDateTime(null);
      setLocation("");
      setIsHarinamComplete(false);
      setPhoneNumber(""); // Reset phone number input
      setResetCalendarTrigger((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      notification(
        "error",
        err.response?.data?.message || t("Scheduling Error")
      );
    }
  };

  const handleRecordingScoreSave = async () => {
    if (!sankirtanDuration) {
      notification("warning", t("Please enter duration of Sankirtan."));
      return;
    }

    if (!sankirtanDate) {
      notification("warning", t("Please select Sankirtan date."));
      return;
    }

    const month = new Date(sankirtanDate).getMonth() + 1; // Extract numeric month (1-12)
    const year = new Date(sankirtanDate).getFullYear(); // Extract numeric month (1-12)

    // 🧮 Points Calculation
    // 🧮 Points Calculation (Duration + Date Only)
    const durationPointsMap = {
      "0-30 mins": 0,
      "30-60 mins": 5,
      "60-90 mins": 10,
      "90-120 mins": 15,
      "120-150 mins": 20,
      "150-180 mins": 25,
    };

    // Points for duration
    const durationPoints = durationPointsMap[sankirtanDuration] || 0;

    // +5 bonus points if date is selected
    const datePoints = sankirtanDate ? 5 : 0;

    // Final total points
    const totalPoints = durationPoints + datePoints;

    const data = [
      {
        year: year,
        sankirtan_date: sankirtanDate,
        count_book: 1,
        points: totalPoints,
        participants: participantCount,
        outreach: isOutreachNewLocation,
        social_media: isMediaUploaded,
        user_id: user.id,
        user_name: user.name,
        hindiname: user.hindiname,
        temple_id: user.temple_id,
        temple_name: user.temple_name,
        hindi_temple_name: user.hinditempleName,
        temple: user.temple,
        state_id: user.state_id,
        state_name: user.state_name,
        zone: user.zone_name,
        event: "Regular Update",
        sankirtan_date: sankirtanDate, // ⬅️ ISO string from input
        duration: sankirtanDuration, // ⬅️ new field
        month, // ⬅️ extracted numeric month
        photo_files: photoFiles, // ⬅️ new field for photo files
      },
    ];

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("data", JSON.stringify(data));

      if (photoFiles.length > 0) {
        photoFiles.forEach((file, fileIndex) => {
          formData.append("files", file);
          formData.append("fileImageIndex", fileIndex);  // ⭐ REQUIRED
        });
      }

      formData.append("image_locations", JSON.stringify(imageLocations));

      await ApiService.recordingScoreSubmit(formData);
      notification("success", t("Score Submitted Successfully"));
      setPhotoFiles([]);
      setFileInputKey(Date.now()); // ← this recreates the input and clears placeholder
      setSankirtanDate("");
      setParticipantCount(0);
      setIsOutreachNewLocation("");
      setSankirtanDuration("");
      getScoreCountData();
      fetchDataFromAllLiveRankingByUserId();
    } catch (err) {
      console.error(err);
      notification(
        "error",
        err.response?.data?.message || t("Submission failed")
      );
    } finally {
      setLoading(false);
    }
  };

  const getScoreCountData = async () => {
    try {
      const res = await ApiService.fetchScoreCount(
        user.temple_id,
        "all",
        true,
        displayYear,
        displayMonth
      );
      if (Array.isArray(res) && res.length) {
        setHarinaamCounts(parseInt(res[0].count_book, 10) || 0);
      } else {
        setHarinaamCounts(0);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDataFromAllLiveRankingByUserId = async () => {
    try {
      if (user) {
        const res = await ApiService.fetchDataFromAllLiveRankingByUserId(
          user.temple_id,
          user.zone_name,
          user.temple,
          user.state_id,
          selectedEvent
        );
        setUserScoreData(res);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const calculatePoints = (bookType) => {
      const count = counts[bookType];
      const pointPerBook = POINTS_PER_BOOK[bookType];
      setPoints((prev) => ({ ...prev, [bookType]: count * pointPerBook }));
    };
    Object.keys(counts).forEach(calculatePoints);
  }, [counts]);

  useEffect(() => {
    if (user) {
      getScoreCountData();
      fetchDataFromAllLiveRankingByUserId();
    }
  }, [user, selectedEvent, selectedYear, selectedMonth]);

  useEffect(() => {
    if (user) {
      getScoreCountData();
    }
  }, [displayMonth, displayYear]);

  useEffect(() => {
    const outreachPoints = isOutreachNewLocation === "Yes" ? 5 : 0;
    const mediaPoints = isMediaUploaded === "Yes" ? 5 : 0;

    const durationPointsMap = {
      "0-30 mins": 0,
      "30-60 mins": 5,
      "60-90 mins": 10,
      "90-120 mins": 15,
      "120-150 mins": 20,
      "150-180 mins": 25,
    };
    const durationPoints = durationPointsMap[sankirtanDuration] || 0;

    // ✅ Add +5 points if Sankirtan date is selected
    const datePoints = sankirtanDate ? 5 : 0;

    setLocalPoints(outreachPoints + mediaPoints + durationPoints + datePoints);
  }, [
    isOutreachNewLocation,
    isMediaUploaded,
    sankirtanDuration,
    sankirtanDate, // ← include sankirtanDate in dependency
  ]);

  const handlePhotoSelect = async (e) => {
    const files = [...e.target.files];
    setPhotoFiles(files);

    setSelectedPhotoNames(files.map(file => file.name));
    const extracted = [];

    for (const file of files) {
      try {
        const meta = await exifr.parse(file, true);
        console.log("EXIF metadata for", file.name, ":", meta);


        let latitude = null;
        let longitude = null;
        let source = "none";

        // ---- EXTRACT GPS ----
        if (meta?.latitude && meta?.longitude) {
          latitude = meta.latitude;
          longitude = meta.longitude;
          source = "exif";
        }

        // ---- EXTRACT BOTH DATE FIELDS ----
        const dto = meta?.DateTimeOriginal instanceof Date ? meta.DateTimeOriginal : null;
        let cdate = null;

        if (meta?.CreateDate) {
          try {
            cdate = new Date(meta.CreateDate);   // Convert string → Date
          } catch (e) {
            cdate = null;
          }
        }

        // formatting helpers
        const formatDate = (d) => d.toISOString().split("T")[0];
        const formatTime = (d) => d.toTimeString().split(" ")[0];

        // final fields
        let taken_date = dto ? formatDate(dto) : null;
        let taken_time = dto ? formatTime(dto) : null;

        let created_date = cdate ? formatDate(cdate) : null;
        let created_time = cdate ? formatTime(cdate) : null;

        // ---- FALLBACK IF NO EXIF DATES ----
        if (!taken_date || !taken_time) {
          const now = new Date();
          taken_date = formatDate(now);
          taken_time = formatTime(now);
        }

        if (!created_date || !created_time) {
          const now = new Date();
          created_date = formatDate(now);
          created_time = formatTime(now);
        }

        // ---- FALLBACK GPS USING DEVICE ----
        if (!latitude || !longitude) {
          await new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                latitude = pos.coords.latitude;
                longitude = pos.coords.longitude;
                source = "device";
                resolve();
              },
              () => resolve() // user denied
            );
          });
        }

        // ---- PUSH FINAL RESULT ----
        extracted.push({
          name: file.name,
          latitude,
          longitude,
          source,

          // new fields
          taken_date,
          taken_time,
          created_date,
          created_time,
        });

      } catch (error) {
        console.log("EXIF error:", error);

        extracted.push({
          name: file.name,
          latitude: null,
          longitude: null,
          source: "error",
          taken_date: null,
          taken_time: null,
          created_date: null,
          created_time: null,
        });
      }
    }

    console.log("EXTRACTED:", extracted);
    setImageLocations(extracted);
  };

  const handleBulkUpdateSave = async (rows) => {
    try {
      if (!rows || !rows.length) {
        notification(
          "warning",
          t("Please add at least one row for bulk update.")
        );
        return;
      }

      const durationPointsMap = {
        "0-30 mins": 0,
        "30-60 mins": 5,
        "60-90 mins": 10,
        "90-120 mins": 15,
        "120-150 mins": 20,
        "150-180 mins": 25,
      };

      const data = rows.map((row, index) => {
        const formattedDate = `${row.year}-${String(row.month + 1).padStart(
          2,
          "0"
        )}-${String(row.day).padStart(2, "0")}`;
        const month = row.month + 1;
        const year = row.year;
        const durationPoints = durationPointsMap[row.duration] || 0;
        const datePoints = 5; // date always selected in bulk
        const totalPoints = durationPoints + datePoints;

        return {
          __index: index,
          year,
          count_book: 1,
          points: totalPoints,
          participants: Number(row.peoples) || 0,
          outreach: "", // not using outreach/media in bulk
          social_media: "",
          user_id: user.id,
          user_name: user.name,
          hindiname: user.hindiname,
          temple_id: user.temple_id,
          temple_name: user.temple_name,
          hindi_temple_name: user.hinditempleName,
          temple: user.temple,
          state_id: user.state_id,
          state_name: user.state_name,
          zone: user.zone_name,
          event: "Regular Update",
          sankirtan_date: formattedDate,
          duration: row.duration,
          month,
        };
      });

      const formData = new FormData();
      formData.append("data", JSON.stringify(data));

      // Append all images (no per-row mapping on backend currently)
      // Append all images + EXIF metadata
      rows.forEach((row, index) => {
        if (row.images && row.images.length) {
          row.images.forEach((file, fileIndex) => {
            formData.append("files", file);
            formData.append("fileRowIndex", index); // tells backend which row file belongs to
            formData.append("fileImageIndex", fileIndex); // optional, useful for mapping
          });
        }
      });

      // SEND EXIF METADATA
      const allImageMeta = rows.map((row) => row.imageLocations || []);

      formData.append("image_locations", JSON.stringify(allImageMeta));

      setLoading(true);
      await ApiService.recordingScoreSubmit(formData);
      notification("success", t("Bulk scores submitted successfully"));

      setShowBulkUpdate(false);
      getScoreCountData();
      fetchDataFromAllLiveRankingByUserId();
    } catch (err) {
      console.error(err);
      notification(
        "error",
        err.response?.data?.message || t("Bulk submission failed")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBulkScheduleSave = async (rows) => {
    try {
      if (!rows || rows.length === 0) {
        notification("error", t("Please select at least one date."));
        return;
      }

      // Validate every row
      for (const r of rows) {
        if (!r.date_time) {
          notification("error", t("Date & time cannot be empty."));
          return;
        }
        if (!r.location || r.location.trim() === "") {
          notification("error", t("Location cannot be empty."));
          return;
        }
      }

      // Convert rows into backend format
      const data = rows.map((r) => {
        // Split date_time into two parts
        const [datePart, timePart] = r.date_time.split(" ");

        return {
          location: r.location,
          temple: user.temple_name,
          date: new Date(datePart).toISOString(), // ✔ send date correctly
          time: timePart, // ✔ send time separately
          phone_number: user.phone_number || "",
        };
      });

      await ApiService.saveSchedule(data);

      notification("success", t("Bulk Sankirtan scheduled successfully"));
      setShowBulkSchedule(false);
      setResetCalendarTrigger((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      notification(
        "error",
        err.response?.data?.message || t("Bulk scheduling failed")
      );
    }
  };


  return (
    <>
      <ToastContainer />
      <div className="container common-container pledge-responsive update-responsive">
        <div className="update-score-container">
          <div className="row">
            <div className="col-md-6">
              <div className="card card-shadow">
                <div className="card-body">
                  <h5 className="card-title">
                    {t("Update Your Sankirtan Score")}
                  </h5>
                  <p className="card-text-bottom">
                    {t(
                      "View and update your harinaam sankirtan score to stay on track with your monthly commitment."
                    )}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card card-shadow">
                <div className="card-body">
                  <h5 className="stats-header">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 31 31"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        opacity="0.5"
                        d="M27 0C29.2091 0 31 1.79086 31 4V27C31 29.2091 29.2091 31 27 31H4C1.79086 31 0 29.2091 0 27V4C0 1.79086 1.79086 0 4 0H27Z"
                        fill="white"
                      />
                      <path
                        d="M9.87303 21.1259H21.9286C22.3725 21.1259 22.7323 21.4858 22.7323 21.9296C22.7323 22.3735 22.3725 22.7333 21.9286 22.7333H9.06933C8.62546 22.7333 8.26562 22.3735 8.26562 21.9296V9.07037C8.26562 8.6265 8.62546 8.26667 9.06933 8.26667C9.5132 8.26667 9.87303 8.6265 9.87303 9.07037V21.1259Z"
                        fill="#FF968C"
                      />
                      <path
                        d="M12.871 17.6571C12.5674 17.9809 12.0588 17.9973 11.735 17.6938C11.4112 17.3902 11.3947 16.8816 11.6983 16.5577L14.7122 13.3429C15.0058 13.0297 15.4937 13.0026 15.8203 13.2812L18.199 15.3111L21.2983 11.3853C21.5733 11.0369 22.0787 10.9775 22.4271 11.2525C22.7755 11.5276 22.835 12.033 22.5599 12.3814L18.9433 16.9625C18.6608 17.3203 18.1375 17.3718 17.7907 17.0758L15.3603 15.0018L12.871 17.6571Z"
                        fill="#FF968C"
                      />
                    </svg>
                    &nbsp;{t("Your Stats")} {""}
                    {selectedEvent !== "Select Event" &&
                      selectedEvent !== "Regular Update" &&
                      t("in") + " " + t(selectedEvent)}
                  </h5>

                  <div className="card-text stats">
                    <div className="text-center">
                      <p className="ranking-status-text">
                        {t("All India Rank")}
                      </p>
                      <span className="ranking-count">
                        {userScoreData.all_rank ?? "-"}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">{t("State Rank")}</p>
                      <span className="ranking-count">
                        {userScoreData.state_rank ?? "-"}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">{t("Zonal Rank")}</p>
                      <span className="ranking-count">
                        {userScoreData.zone_rank ?? "-"}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">{t("Total Points")}</p>
                      <span className="ranking-count">
                        {userScoreData?.points
                          ? Math.ceil(userScoreData?.points)?.toLocaleString(
                            "en-IN"
                          )
                          : "-"}
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="ranking-status-text">
                        {t("Sankirtan Count")}
                      </p>
                      <span className="ranking-count">
                        {userScoreData?.count_book
                          ? Math.ceil(
                            userScoreData?.count_book
                          )?.toLocaleString("en-IN")
                          : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="update-score-container mt-4">
            <div className="row">
              <div className="col-6">
                <div className="content-table">
                  <div className="d-flex justify-content-between">
                    <div className="table-description">
                      <h3 className="content-title">
                        {t("Schedule Your Next Harinaam Sankirtan")}
                      </h3>
                      <p className="text-muted text-main-class">
                        {t(
                          "Please enter date, time and location of sankirtan."
                        )}
                      </p>
                    </div>
                    <button
                      className="btn-primarysave custom-button"
                      onClick={() => setShowBulkSchedule(true)}
                    >
                      Bulk Schedule
                    </button>
                  </div>
                  <hr />

                  <div className="card-body-middle">
                    {/* Replaced with Calendar Modal Component */}
                    <CalendarComponent
                      onDateTimeSelect={handleDateTimeSelect}
                      resetTrigger={resetCalendarTrigger}
                    />
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Enter Location")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Enter the location of Sankirtan.")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          style={{ textAlign: "center" }}
                          value={location}
                          onChange={(e) => setLocation(e.target.value)}
                        />
                      </div>
                    </div>
                    {/* <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Enter Phone Number")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Enter the mobile number")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="tel"
                          className="form-control text-center"
                          value={phoneNumber}
                          onChange={(e) => {
                            // Remove non-digit characters and limit length to 10
                            const cleaned = e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10);
                            setPhoneNumber(cleaned);
                          }}
                          placeholder=""
                          maxLength={10}
                        />
                      </div>
                    </div> */}
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-6 fw-medium mobile-font">
                        {t("Total Harinam Sankirtan Count")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Total Harinaam Sankirtan performed")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="tel"
                          className="form-control text-center"
                          disabled
                          value={userScoreData?.count_book || "0"}
                          placeholder=""
                          maxLength={10}
                        />
                      </div>
                    </div>
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-6 fw-medium mobile-font">
                        {t("Your Total Harinaam Sankirtan Score")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Total Harinaam Sankirtan score")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="tel"
                          className="form-control text-center"
                          disabled
                          value={userScoreData?.points || "0"}
                          placeholder=""
                          maxLength={10}
                        />
                      </div>
                    </div>
                  </div>
                  <hr style={{ marginTop: "5%" }} />
                  <div className="d-flex justify-content-between align-items-center">
                    <small className="text-muted col-md-7 text-main-class">
                      {t("If you wish to do any changes, please contact us.")}
                    </small>
                    <button
                      className="btn-primarysave custom-button"
                      onClick={harinaamScheduleSave}
                    >
                      {t("Schedule Sankirtan")}
                    </button>
                  </div>
                </div>
              </div>

              <div className="col-6">
                <div className="content-table">
                  <div className="d-flex justify-content-between">
                    <div className="table-description">
                      <h3 className="content-title">
                        {t("Update Your Harinaam Sankirtan Score")}
                      </h3>
                      <p className="text-muted text-main-class">
                        {t("Please enter your Harinaam Sankirtan score")}
                      </p>
                    </div>
                    <button
                      className="btn-primarysave custom-button"
                      onClick={() => setShowBulkUpdate(true)}
                    >
                      Bulk Update
                    </button>
                  </div>
                  <hr />

                  <div className="card-body-middle">
                    {/* Date of Sankirtan */}
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Date of Sankirtan")}
                        <br />
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Select the date of the month.")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="date"
                          className="form-control"
                          style={{
                            colorScheme: "light",
                            color: sankirtanDate ? "#000" : "#6b7280",
                          }}
                          value={sankirtanDate}
                          onChange={(e) => setSankirtanDate(e.target.value)}
                          max={new Date().toISOString().split("T")[0]} // This sets today as the max date
                        />
                      </div>
                    </div>

                    {/* Outreach to New Location? */}
                    {/* <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Outreach to New Location?")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("(Yes = +5 points)")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select"
                          style={{
                            color: isOutreachNewLocation ? "#000" : "#6b7280",
                          }}
                          value={isOutreachNewLocation}
                          onChange={(e) =>
                            setIsOutreachNewLocation(e.target.value)
                          }
                        >
                          <option
                            value=""
                            disabled
                            style={{ color: "#6b7280" }}
                          >
                            {t("Select")}
                          </option>
                          <option value="Yes" style={{ color: "#000" }}>
                            {t("Yes")}
                          </option>
                          <option value="No" style={{ color: "#000" }}>
                            {t("No")}
                          </option>
                        </select>
                      </div>
                    </div> */}

                    {/* Images/Videos uploaded to Social Media */}
                    {/* <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Images/Videos uploaded to Social Media?")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("(Yes = +5 points)")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select"
                          style={{
                            color: isMediaUploaded ? "#000" : "#6b7280",
                          }}
                          value={isMediaUploaded}
                          onChange={(e) => setIsMediaUploaded(e.target.value)}
                        >
                          <option
                            value=""
                            disabled
                            style={{ color: "#6b7280" }}
                          >
                            {t("Select")}
                          </option>
                          <option value="Yes" style={{ color: "#000" }}>
                            {t("Yes")}
                          </option>
                          <option value="No" style={{ color: "#000" }}>
                            {t("No")}
                          </option>
                        </select>
                      </div>
                    </div> */}

                    {/* Duration of Sankirtan */}
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Duration of Sankirtan")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("(Longer Sankirtan earns more points)")}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <select
                          className="form-select"
                          style={{
                            color: sankirtanDuration ? "#000" : "#6b7280",
                          }}
                          value={sankirtanDuration}
                          onChange={(e) => setSankirtanDuration(e.target.value)}
                        >
                          <option value="" disabled>
                            {t("Select Duration")}
                          </option>
                          <option value="0-30 mins">0–30 mins (0 pts)</option>
                          <option value="30-60 mins">30–60 mins (5 pts)</option>
                          <option value="60-90 mins">
                            60–90 mins (10 pts)
                          </option>
                          <option value="90-120 mins">
                            90–120 mins (15 pts)
                          </option>
                          <option value="120-150 mins">
                            120–150 mins (20 pts)
                          </option>
                          <option value="150-180 mins">
                            150–180 mins (25 pts)
                          </option>
                        </select>
                      </div>
                    </div>

                    {/* Number of Participants */}
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-5 fw-medium mobile-font">
                        {t("Number of Participants")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("Enter the no.of members in the Sankirtan.")}{" "}
                        </p>
                      </div>
                      <div className="col-md-4">
                        <input
                          type="number"
                          min={0}
                          max={1000}
                          className="form-control placeholder:text-gray-500 text-black"
                          style={{ textAlign: "left" }}
                          placeholder="0"
                          value={participantCount === 0 ? "" : participantCount}
                          onChange={(e) => {
                            const val = e.target.value;
                            setParticipantCount(
                              val === "" ? 0 : Math.min(Number(val), 1000)
                            );
                          }}
                        />
                      </div>
                    </div>

                    {/* Upload Photos */}
                    <div className="row mb-3 d-flex justify-content-between">
                      <div className="col-md-8 fw-medium mobile-font">
                        {t("Upload Photos")}
                        <p
                          style={{
                            color: "#6E717FE5",
                            marginTop: "2%",
                            fontSize: "14px",
                          }}
                        >
                          {t("(Upload Sankirtan Photos)")}
                        </p>
                      </div>

                      <div className="col-md-4 mt-2">
                        {/* Hidden file input */}
                        {/* Hidden file input */}
                        <input
                          key={fileInputKey}
                          ref={photoInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden-file-input"
                          onChange={handlePhotoSelect}
                        />

                        {/* Custom SVG upload button */}
                        <div
                          onClick={() => photoInputRef.current.click()}
                          style={{
                            width: "100%",          // FIX 1: responsive width
                            maxWidth: "170px",      // FIX 2: desktop size limit
                            height: "50px",
                            cursor: "pointer",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: "8px",
                            border: "1px dashed #aaa",
                            marginTop: "6px",
                          }}
                        >
                          {/* Your SVG here */}
                          <svg
                            width="194"
                            height="45"
                            viewBox="0 0 194 45"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M182 0V0.5H183C183.348 0.5 183.691 0.516833 184.03 0.549805L184.078 0.0527344C184.806 0.123583 185.514 0.2658 186.193 0.47168L186.049 0.949219C186.713 1.15031 187.349 1.41536 187.95 1.7373L188.186 1.29785C188.823 1.6392 189.423 2.04063 189.978 2.49609L189.661 2.88281C190.193 3.3195 190.681 3.80738 191.117 4.33887L191.503 4.02148C191.958 4.5759 192.36 5.17618 192.701 5.81348L192.263 6.0498C192.585 6.65091 192.85 7.28745 193.051 7.95117L193.527 7.80566C193.733 8.48566 193.875 9.1932 193.946 9.92188L193.45 9.96973C193.483 10.3085 193.5 10.6523 193.5 11V11.958H194V13.875H193.5V15.792H194V17.708H193.5V19.625H194V21.542H193.5V23.458H194V25.375H193.5V27.292H194V29.208H193.5V31.125H194V33.042H193.5V34C193.5 34.3477 193.483 34.6915 193.45 35.0303L193.946 35.0771C193.875 35.8058 193.733 36.5133 193.527 37.1934L193.051 37.0488C192.85 37.7125 192.585 38.3491 192.263 38.9502L192.701 39.1855C192.36 39.8229 191.958 40.4231 191.503 40.9775L191.117 40.6611C190.681 41.1926 190.193 41.6805 189.661 42.1172L189.978 42.5029C189.423 42.9585 188.823 43.3598 188.186 43.7012L187.95 43.2627C187.349 43.5846 186.713 43.8497 186.049 44.0508L186.193 44.5273C185.513 44.7334 184.806 44.8755 184.077 44.9463L184.03 44.4502C183.691 44.4832 183.348 44.5 183 44.5H182V45H180V44.5H178V45H176V44.5H174V45H172V44.5H170V45H168V44.5H166V45H164V44.5H162V45H160V44.5H158V45H156V44.5H154V45H152V44.5H150V45H148V44.5H146V45H144V44.5H142V45H140V44.5H138V45H136V44.5H134V45H132V44.5H130V45H128V44.5H126V45H124V44.5H122V45H120V44.5H118V45H116V44.5H114V45H112V44.5H110V45H108V44.5H106V45H104V44.5H102V45H100V44.5H98V45H96V44.5H94V45H92V44.5H90V45H88V44.5H86V45H84V44.5H82V45H80V44.5H78V45H76V44.5H74V45H72V44.5H70V45H68V44.5H66V45H64V44.5H62V45H60V44.5H58V45H56V44.5H54V45H52V44.5H50V45H48V44.5H46V45H44V44.5H42V45H40V44.5H38V45H36V44.5H34V45H32V44.5H30V45H28V44.5H26V45H24V44.5H22V45H20V44.5H18V45H16V44.5H14V45H12V44.5H11C10.6523 44.5 10.3085 44.4832 9.96973 44.4502L9.92188 44.9463C9.1932 44.8754 8.48566 44.7334 7.80566 44.5273L7.95117 44.0508C7.28745 43.8497 6.65091 43.5846 6.0498 43.2627L5.81348 43.7012C5.17618 43.3597 4.5759 42.9585 4.02148 42.5029L4.33887 42.1172C3.80738 41.6805 3.3195 41.1926 2.88281 40.6611L2.49609 40.9775C2.04063 40.4231 1.6392 39.8229 1.29785 39.1855L1.7373 38.9502C1.41536 38.3491 1.15031 37.7125 0.949219 37.0488L0.47168 37.1934C0.265708 36.5134 0.123552 35.8058 0.0527344 35.0771L0.549805 35.0303C0.516833 34.6915 0.5 34.3477 0.5 34V33.042H0V31.125H0.5V29.208H0V27.292H0.5V25.375H0V23.458H0.5V21.542H0V19.625H0.5V17.708H0V15.792H0.5V13.875H0V11.958H0.5V11C0.5 10.6523 0.516833 10.3085 0.549805 9.96973L0.0527344 9.92188C0.123615 9.19326 0.265653 8.48561 0.47168 7.80566L0.949219 7.95117C1.15031 7.28745 1.41536 6.65091 1.7373 6.0498L1.29785 5.81348C1.63923 5.17621 2.04061 4.57588 2.49609 4.02148L2.88281 4.33887C3.3195 3.80738 3.80738 3.3195 4.33887 2.88281L4.02148 2.49609C4.57588 2.04061 5.17621 1.63923 5.81348 1.29785L6.0498 1.7373C6.65091 1.41536 7.28745 1.15031 7.95117 0.949219L7.80566 0.47168C8.48561 0.265653 9.19326 0.123615 9.92188 0.0527344L9.96973 0.549805C10.3085 0.516833 10.6523 0.5 11 0.5H12V0H14V0.5H16V0H18V0.5H20V0H22V0.5H24V0H26V0.5H28V0H30V0.5H32V0H34V0.5H36V0H38V0.5H40V0H42V0.5H44V0H46V0.5H48V0H50V0.5H52V0H54V0.5H56V0H58V0.5H60V0H62V0.5H64V0H66V0.5H68V0H70V0.5H72V0H74V0.5H76V0H78V0.5H80V0H82V0.5H84V0H86V0.5H88V0H90V0.5H92V0H94V0.5H96V0H98V0.5H100V0H102V0.5H104V0H106V0.5H108V0H110V0.5H112V0H114V0.5H116V0H118V0.5H120V0H122V0.5H124V0H126V0.5H128V0H130V0.5H132V0H134V0.5H136V0H138V0.5H140V0H142V0.5H144V0H146V0.5H148V0H150V0.5H152V0H154V0.5H156V0H158V0.5H160V0H162V0.5H164V0H166V0.5H168V0H170V0.5H172V0H174V0.5H176V0H178V0.5H180V0H182Z"
                              fill="white"
                              stroke="#C3C6D9"
                              stroke-dasharray="2 2"
                            />
                            <path
                              d="M63.6414 33.96H62.7614C61.6947 32.6 61.1614 31.0767 61.1614 29.39C61.1614 28.5767 61.2981 27.7833 61.5714 27.01C61.8447 26.2367 62.2181 25.5433 62.6914 24.93H63.5614C62.5147 26.3433 61.9914 27.8433 61.9914 29.43C61.9914 31.03 62.5414 32.54 63.6414 33.96ZM64.2371 32L64.7271 25.26H65.7571L66.6471 27.72L67.7271 30.86C68.1138 29.7667 68.4938 28.72 68.8671 27.72L69.7971 25.26H70.8271L71.4171 32H70.6071L70.3771 29.34L70.1371 26.35C69.7838 27.3367 69.4204 28.3333 69.0471 29.34L68.1071 31.85H67.3371L66.4471 29.34L65.4271 26.41C65.4271 26.67 65.3638 27.6467 65.2371 29.34L65.0471 32H64.2371ZM75.5713 29.66H74.6913C74.2647 29.66 73.9447 29.7433 73.7313 29.91C73.518 30.0767 73.4113 30.2867 73.4113 30.54C73.4113 30.7933 73.4947 31 73.6613 31.16C73.828 31.3133 74.0713 31.39 74.3913 31.39C74.7513 31.39 75.038 31.29 75.2513 31.09C75.4647 30.8833 75.5713 30.6233 75.5713 30.31V29.66ZM72.9513 27.99V27.2C73.3313 26.9667 73.8313 26.85 74.4513 26.85C75.7247 26.85 76.3613 27.4967 76.3613 28.79V32H75.5813V31.4C75.3213 31.8333 74.848 32.05 74.1613 32.05C73.688 32.05 73.3047 31.91 73.0113 31.63C72.7247 31.35 72.5813 30.9967 72.5813 30.57C72.5813 30.0833 72.7647 29.7067 73.1313 29.44C73.5047 29.1667 74.0113 29.03 74.6513 29.03H75.5713V28.77C75.5713 28.37 75.4813 28.07 75.3013 27.87C75.1213 27.6633 74.818 27.56 74.3913 27.56C73.8513 27.56 73.3713 27.7033 72.9513 27.99ZM79.8632 29.41L81.5632 32H80.6532L79.4232 30L78.1532 32H77.2832L78.9832 29.4L77.3832 26.92H78.2932L79.4432 28.82L80.6032 26.92H81.5032L79.8632 29.41ZM82.2186 31.91C82.1053 31.7967 82.0486 31.6567 82.0486 31.49C82.0486 31.3233 82.1053 31.1833 82.2186 31.07C82.3319 30.9567 82.4719 30.9 82.6386 30.9C82.8053 30.9 82.9453 30.9567 83.0586 31.07C83.1719 31.1833 83.2286 31.3233 83.2286 31.49C83.2286 31.6567 83.1719 31.7967 83.0586 31.91C82.9453 32.0233 82.8053 32.08 82.6386 32.08C82.4719 32.08 82.3319 32.0233 82.2186 31.91ZM90.3802 25.97H87.4902V28.36H90.0302V29.07H87.4902V32H86.6802V25.26H90.3802V25.97ZM91.5727 32V26.93H92.3627V32H91.5727ZM91.5927 25.71C91.4994 25.61 91.4527 25.4867 91.4527 25.34C91.4527 25.1933 91.4994 25.07 91.5927 24.97C91.6927 24.87 91.8161 24.82 91.9627 24.82C92.1094 24.82 92.2327 24.87 92.3327 24.97C92.4327 25.07 92.4827 25.1933 92.4827 25.34C92.4827 25.4867 92.4327 25.61 92.3327 25.71C92.2327 25.8033 92.1094 25.85 91.9627 25.85C91.8161 25.85 91.6927 25.8033 91.5927 25.71ZM93.9067 32V24.93H94.6967V32H93.9067ZM99.4307 29.14V28.94C99.4307 28.52 99.3174 28.1867 99.0907 27.94C98.864 27.6867 98.5507 27.56 98.1507 27.56C97.7707 27.56 97.4474 27.6967 97.1807 27.97C96.914 28.2433 96.7574 28.6333 96.7107 29.14H99.4307ZM99.9007 31V31.74C99.5074 31.9467 99.0274 32.05 98.4607 32.05C97.694 32.05 97.074 31.8167 96.6007 31.35C96.134 30.8767 95.9007 30.2533 95.9007 29.48C95.9007 28.6733 96.1107 28.0367 96.5307 27.57C96.9507 27.1033 97.4907 26.87 98.1507 26.87C98.784 26.87 99.2874 27.0733 99.6607 27.48C100.034 27.8867 100.221 28.4633 100.221 29.21C100.221 29.4233 100.204 29.62 100.171 29.8H96.7107C96.7707 30.3 96.9607 30.6833 97.2807 30.95C97.6007 31.2167 98.0207 31.35 98.5407 31.35C99.1074 31.35 99.5607 31.2333 99.9007 31ZM106.627 27.1V27.85C106.32 27.6633 105.9 27.57 105.367 27.57C105.1 27.57 104.887 27.6333 104.727 27.76C104.573 27.88 104.497 28.0367 104.497 28.23C104.497 28.35 104.52 28.4567 104.567 28.55C104.613 28.6367 104.703 28.7233 104.837 28.81C104.977 28.8967 105.077 28.9567 105.137 28.99C105.203 29.0167 105.343 29.0767 105.557 29.17C105.983 29.3433 106.317 29.54 106.557 29.76C106.797 29.98 106.917 30.2767 106.917 30.65C106.917 31.0833 106.763 31.4267 106.457 31.68C106.157 31.9333 105.743 32.06 105.217 32.06C104.603 32.06 104.107 31.9567 103.727 31.75V30.98C104.173 31.2467 104.653 31.38 105.167 31.38C105.473 31.38 105.707 31.32 105.867 31.2C106.027 31.08 106.107 30.9167 106.107 30.71C106.107 30.47 106.017 30.2833 105.837 30.15C105.663 30.0167 105.37 29.8667 104.957 29.7C104.577 29.5533 104.27 29.37 104.037 29.15C103.81 28.93 103.697 28.6333 103.697 28.26C103.697 27.84 103.853 27.5067 104.167 27.26C104.487 27.0067 104.883 26.88 105.357 26.88C105.883 26.88 106.307 26.9533 106.627 27.1ZM108.028 32V26.93H108.818V32H108.028ZM108.048 25.71C107.954 25.61 107.908 25.4867 107.908 25.34C107.908 25.1933 107.954 25.07 108.048 24.97C108.148 24.87 108.271 24.82 108.418 24.82C108.564 24.82 108.688 24.87 108.788 24.97C108.888 25.07 108.938 25.1933 108.938 25.34C108.938 25.4867 108.888 25.61 108.788 25.71C108.688 25.8033 108.564 25.85 108.418 25.85C108.271 25.85 108.148 25.8033 108.048 25.71ZM113.542 26.93V27.51L110.942 31.29H113.682V32H110.032V31.38L112.602 27.63H110.132V26.93H113.542ZM118.083 29.14V28.94C118.083 28.52 117.97 28.1867 117.743 27.94C117.516 27.6867 117.203 27.56 116.803 27.56C116.423 27.56 116.1 27.6967 115.833 27.97C115.566 28.2433 115.41 28.6333 115.363 29.14H118.083ZM118.553 31V31.74C118.16 31.9467 117.68 32.05 117.113 32.05C116.346 32.05 115.726 31.8167 115.253 31.35C114.786 30.8767 114.553 30.2533 114.553 29.48C114.553 28.6733 114.763 28.0367 115.183 27.57C115.603 27.1033 116.143 26.87 116.803 26.87C117.436 26.87 117.94 27.0733 118.313 27.48C118.686 27.8867 118.873 28.4633 118.873 29.21C118.873 29.4233 118.856 29.62 118.823 29.8H115.363C115.423 30.3 115.613 30.6833 115.933 30.95C116.253 31.2167 116.673 31.35 117.193 31.35C117.76 31.35 118.213 31.2333 118.553 31ZM119.938 28.2C119.938 28.04 119.991 27.9033 120.098 27.79C120.211 27.6767 120.351 27.62 120.518 27.62C120.684 27.62 120.824 27.6767 120.938 27.79C121.051 27.9033 121.108 28.04 121.108 28.2C121.108 28.3667 121.051 28.5067 120.938 28.62C120.824 28.7267 120.684 28.78 120.518 28.78C120.351 28.78 120.211 28.7267 120.098 28.62C119.991 28.5067 119.938 28.3667 119.938 28.2ZM120.098 31.85C119.991 31.7367 119.938 31.5967 119.938 31.43C119.938 31.2633 119.991 31.1233 120.098 31.01C120.211 30.8967 120.351 30.84 120.518 30.84C120.684 30.84 120.824 30.8967 120.938 31.01C121.051 31.1233 121.108 31.2633 121.108 31.43C121.108 31.5967 121.051 31.7367 120.938 31.85C120.824 31.9567 120.684 32.01 120.518 32.01C120.351 32.01 120.211 31.9567 120.098 31.85ZM124.323 27.25V26.4L125.873 25.46H126.613V32H125.833V26.32L124.323 27.25ZM130.516 32L131.006 25.26H132.036L132.926 27.72L134.006 30.86C134.393 29.7667 134.773 28.72 135.146 27.72L136.076 25.26H137.106L137.696 32H136.886L136.656 29.34L136.416 26.35C136.063 27.3367 135.7 28.3333 135.326 29.34L134.386 31.85H133.616L132.726 29.34L131.706 26.41C131.706 26.67 131.643 27.6467 131.516 29.34L131.326 32H130.516ZM139.971 31.31H141.501C141.927 31.31 142.254 31.2133 142.481 31.02C142.714 30.82 142.831 30.53 142.831 30.15C142.831 29.77 142.701 29.4733 142.441 29.26C142.187 29.0467 141.824 28.94 141.351 28.94H139.971V31.31ZM139.971 25.95V28.25H141.051C141.591 28.25 141.991 28.1367 142.251 27.91C142.511 27.6833 142.641 27.39 142.641 27.03C142.641 26.7167 142.517 26.46 142.271 26.26C142.031 26.0533 141.671 25.95 141.191 25.95H139.971ZM139.161 25.26H141.191C141.884 25.26 142.437 25.42 142.851 25.74C143.271 26.06 143.481 26.49 143.481 27.03C143.481 27.4167 143.371 27.7533 143.151 28.04C142.937 28.32 142.661 28.4967 142.321 28.57C142.694 28.63 143.011 28.8 143.271 29.08C143.537 29.3533 143.671 29.7267 143.671 30.2C143.671 30.74 143.491 31.1767 143.131 31.51C142.777 31.8367 142.274 32 141.621 32H139.161V25.26ZM144.964 24.93C145.437 25.5433 145.81 26.2367 146.084 27.01C146.357 27.7833 146.494 28.5767 146.494 29.39C146.494 31.07 145.957 32.5933 144.884 33.96H144.004C145.104 32.54 145.654 31.03 145.654 29.43C145.654 27.83 145.13 26.33 144.084 24.93H144.964Z"
                              fill="#1E1F21"
                            />
                            <rect
                              x="4"
                              y="4"
                              width="37"
                              height="37"
                              rx="8"
                              fill="#F5F5F5"
                            />
                            <path
                              d="M20.1248 27.0521C19.8002 27.0521 19.5311 26.7829 19.5311 26.4583V23.1412L18.9611 23.7112C18.7315 23.9408 18.3515 23.9408 18.1219 23.7112C17.8923 23.4816 17.8923 23.1016 18.1219 22.8721L19.7052 21.2887C19.8715 21.1225 20.1327 21.0671 20.3544 21.1621C20.5761 21.2491 20.7186 21.4708 20.7186 21.7083V26.4583C20.7186 26.7829 20.4494 27.0521 20.1248 27.0521Z"
                              fill="#9F1D21"
                            />
                            <path
                              d="M21.7083 23.8854C21.5579 23.8854 21.4075 23.83 21.2887 23.7113L19.7054 22.1279C19.4758 21.8983 19.4758 21.5183 19.7054 21.2888C19.935 21.0592 20.315 21.0592 20.5446 21.2888L22.1279 22.8721C22.3575 23.1017 22.3575 23.4817 22.1279 23.7113C22.0091 23.83 21.8587 23.8854 21.7083 23.8854Z"
                              fill="#9F1D21"
                            />
                            <path
                              d="M24.8752 31.0104H20.1252C15.8264 31.0104 13.9897 29.1737 13.9897 24.875V20.125C13.9897 15.8262 15.8264 13.9896 20.1252 13.9896H24.0835C24.4081 13.9896 24.6772 14.2587 24.6772 14.5833C24.6772 14.9079 24.4081 15.1771 24.0835 15.1771H20.1252C16.4756 15.1771 15.1772 16.4754 15.1772 20.125V24.875C15.1772 28.5246 16.4756 29.8229 20.1252 29.8229H24.8752C28.5247 29.8229 29.8231 28.5246 29.8231 24.875V20.9166C29.8231 20.5921 30.0922 20.3229 30.4168 20.3229C30.7414 20.3229 31.0106 20.5921 31.0106 20.9166V24.875C31.0106 29.1737 29.1739 31.0104 24.8752 31.0104Z"
                              fill="#9F1D21"
                            />
                            <path
                              d="M30.4168 21.5104H27.2502C24.5427 21.5104 23.4897 20.4575 23.4897 17.75V14.5833C23.4897 14.3458 23.6322 14.1241 23.8539 14.0371C24.0756 13.9421 24.3289 13.9975 24.5031 14.1637L30.8364 20.4971C31.0027 20.6633 31.0581 20.9246 30.9631 21.1462C30.8681 21.3679 30.6543 21.5104 30.4168 21.5104ZM24.6772 16.0162V17.75C24.6772 19.7925 25.2077 20.3229 27.2502 20.3229H28.9839L24.6772 16.0162Z"
                              fill="#9F1D21"
                            />
                            <path
                              d="M67.72 10.248V11.46C67.368 11.268 67.048 11.136 66.76 11.064C66.48 10.984 66.12 10.944 65.68 10.944C64.872 10.944 64.196 11.22 63.652 11.772C63.116 12.316 62.848 13.04 62.848 13.944C62.848 14.864 63.116 15.596 63.652 16.14C64.188 16.684 64.896 16.956 65.776 16.956C66.552 16.956 67.204 16.756 67.732 16.356V17.604C67.236 17.932 66.548 18.096 65.668 18.096C64.46 18.096 63.472 17.72 62.704 16.968C61.936 16.216 61.552 15.208 61.552 13.944C61.552 12.72 61.936 11.724 62.704 10.956C63.48 10.188 64.46 9.804 65.644 9.804C66.508 9.804 67.2 9.952 67.72 10.248ZM69.1416 18V9.516H70.3296V18H69.1416ZM72.083 18V11.916H73.271V18H72.083ZM72.143 10.584C72.007 10.44 71.939 10.264 71.939 10.056C71.939 9.848 72.007 9.672 72.143 9.528C72.287 9.384 72.463 9.312 72.671 9.312C72.879 9.312 73.055 9.384 73.199 9.528C73.343 9.672 73.415 9.848 73.415 10.056C73.415 10.256 73.343 10.428 73.199 10.572C73.055 10.716 72.879 10.788 72.671 10.788C72.463 10.788 72.287 10.72 72.143 10.584ZM75.8524 14.988C75.8684 15.604 76.0564 16.112 76.4164 16.512C76.7764 16.904 77.2804 17.1 77.9284 17.1C78.4084 17.1 78.8564 16.968 79.2724 16.704V17.76C78.8804 18 78.3804 18.12 77.7724 18.12C76.8204 18.12 76.0604 17.82 75.4924 17.22C74.9244 16.62 74.6404 15.872 74.6404 14.976C74.6404 14.104 74.9244 13.36 75.4924 12.744C76.0684 12.12 76.8444 11.808 77.8204 11.808C78.3484 11.808 78.8324 11.92 79.2724 12.144V13.224C78.8804 12.976 78.4364 12.852 77.9404 12.852C77.3084 12.852 76.8004 13.052 76.4164 13.452C76.0404 13.852 75.8524 14.364 75.8524 14.988ZM80.6249 18V9.6H81.8009V18H80.6249ZM83.2049 14.82L85.5929 18H84.1889L81.8489 14.82L84.1409 11.916H85.5209L83.2049 14.82ZM91.102 12.828V16.176C91.102 16.76 91.382 17.052 91.942 17.052C92.27 17.052 92.546 16.964 92.77 16.788V17.844C92.506 17.988 92.182 18.06 91.798 18.06C90.542 18.06 89.914 17.424 89.914 16.152V12.828H88.966V11.916H89.914V10.428H91.102V11.916H92.698V12.828H91.102ZM96.6103 18.108C95.7143 18.108 95.0103 17.816 94.4983 17.232C93.9863 16.648 93.7303 15.908 93.7303 15.012C93.7303 14.1 93.9943 13.344 94.5223 12.744C95.0503 12.136 95.7463 11.832 96.6103 11.832C97.5383 11.832 98.2543 12.128 98.7583 12.72C99.2623 13.304 99.5143 14.052 99.5143 14.964C99.5143 15.876 99.2543 16.628 98.7343 17.22C98.2223 17.812 97.5143 18.108 96.6103 18.108ZM96.5983 12.828C96.0783 12.828 95.6743 13.036 95.3863 13.452C95.0983 13.86 94.9543 14.368 94.9543 14.976C94.9543 15.576 95.1023 16.084 95.3983 16.5C95.6943 16.908 96.0983 17.112 96.6103 17.112C97.1543 17.112 97.5663 16.912 97.8463 16.512C98.1343 16.112 98.2783 15.604 98.2783 14.988C98.2783 14.356 98.1423 13.84 97.8703 13.44C97.5983 13.032 97.1743 12.828 96.5983 12.828ZM108.701 14.928V9.912H109.937V14.904C109.937 15.952 109.665 16.748 109.121 17.292C108.577 17.836 107.825 18.108 106.865 18.108C105.921 18.108 105.177 17.844 104.633 17.316C104.089 16.78 103.817 16.008 103.817 15V9.912H105.053V14.988C105.053 15.676 105.217 16.18 105.545 16.5C105.881 16.812 106.321 16.968 106.865 16.968C108.089 16.968 108.701 16.288 108.701 14.928ZM114.429 17.052C114.901 17.052 115.289 16.868 115.593 16.5C115.897 16.124 116.049 15.6 116.049 14.928C116.049 14.296 115.901 13.796 115.605 13.428C115.309 13.052 114.933 12.864 114.477 12.864C114.021 12.864 113.625 13.032 113.289 13.368C112.961 13.696 112.797 14.22 112.797 14.94C112.797 15.644 112.961 16.172 113.289 16.524C113.625 16.876 114.005 17.052 114.429 17.052ZM112.797 11.916V12.852C113.213 12.172 113.837 11.832 114.669 11.832C115.469 11.832 116.101 12.124 116.565 12.708C117.029 13.284 117.261 14.02 117.261 14.916C117.261 15.836 117.021 16.596 116.541 17.196C116.069 17.788 115.437 18.084 114.645 18.084C113.837 18.084 113.221 17.748 112.797 17.076V20.328H111.621V11.916H112.797ZM118.63 18V9.516H119.818V18H118.63ZM124.067 18.108C123.171 18.108 122.467 17.816 121.955 17.232C121.443 16.648 121.187 15.908 121.187 15.012C121.187 14.1 121.451 13.344 121.979 12.744C122.507 12.136 123.203 11.832 124.067 11.832C124.995 11.832 125.711 12.128 126.215 12.72C126.719 13.304 126.971 14.052 126.971 14.964C126.971 15.876 126.711 16.628 126.191 17.22C125.679 17.812 124.971 18.108 124.067 18.108ZM124.055 12.828C123.535 12.828 123.131 13.036 122.843 13.452C122.555 13.86 122.411 14.368 122.411 14.976C122.411 15.576 122.559 16.084 122.855 16.5C123.151 16.908 123.555 17.112 124.067 17.112C124.611 17.112 125.023 16.912 125.303 16.512C125.591 16.112 125.735 15.604 125.735 14.988C125.735 14.356 125.599 13.84 125.327 13.44C125.055 13.032 124.631 12.828 124.055 12.828ZM131.501 15.288H130.589C130.141 15.288 129.801 15.376 129.569 15.552C129.345 15.728 129.233 15.952 129.233 16.224C129.233 16.504 129.321 16.724 129.497 16.884C129.673 17.044 129.929 17.124 130.265 17.124C130.649 17.124 130.949 17.02 131.165 16.812C131.389 16.596 131.501 16.316 131.501 15.972V15.288ZM128.441 13.368V12.228C128.913 11.956 129.537 11.82 130.313 11.82C131.905 11.82 132.701 12.592 132.701 14.136V18H131.537V17.28C131.225 17.792 130.677 18.048 129.893 18.048C129.317 18.048 128.853 17.884 128.501 17.556C128.157 17.22 127.985 16.796 127.985 16.284C127.985 15.7 128.205 15.248 128.645 14.928C129.085 14.6 129.701 14.436 130.493 14.436H131.501V14.088C131.501 13.672 131.401 13.36 131.201 13.152C131.001 12.936 130.669 12.828 130.205 12.828C129.557 12.828 128.969 13.008 128.441 13.368ZM136.745 17.052C137.209 17.052 137.601 16.876 137.921 16.524C138.241 16.172 138.401 15.636 138.401 14.916C138.401 14.22 138.233 13.704 137.897 13.368C137.569 13.032 137.197 12.864 136.781 12.864C136.309 12.864 135.921 13.052 135.617 13.428C135.313 13.796 135.161 14.324 135.161 15.012C135.161 15.62 135.309 16.112 135.605 16.488C135.909 16.864 136.289 17.052 136.745 17.052ZM138.401 9.516H139.589V18H138.401V17.076C137.977 17.748 137.357 18.084 136.541 18.084C135.741 18.084 135.109 17.784 134.645 17.184C134.181 16.576 133.949 15.844 133.949 14.988C133.949 14.036 134.193 13.272 134.681 12.696C135.169 12.12 135.797 11.832 136.565 11.832C137.357 11.832 137.969 12.168 138.401 12.84V9.516Z"
                              fill="#9F1D21"
                            />
                          </svg>
                        </div>
                        {selectedPhotoNames.length > 0 && (
                          <div style={{ marginTop: "8px", fontSize: "8px", color: "#333" }}>
                            {selectedPhotoNames.map((name, index) => (
                              <div key={index}>{name}</div>
                            ))}
                          </div>
                        )}

                      </div>
                    </div>
                  </div>

                  <hr style={{ marginTop: "6%" }} />
                  <div className="d-flex justify-content-between align-items-center">
                    <div
                      className="d-flex gap-3"
                      style={{
                        width: "40%",
                        backgroundColor: "#FBBF2426",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "8px",
                      }}
                    >
                      <svg
                        width="21"
                        height="18"
                        viewBox="0 0 21 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M20.25 18H0V0H20.25V18Z" stroke="#E5E7EB" />
                        <g clip-path="url(#clip0_6362_549)">
                          <path
                            d="M14.0625 0H6.1875C5.25586 0 4.49648 0.766406 4.53164 1.69453C4.53867 1.88086 4.5457 2.06719 4.55625 2.25H0.84375C0.376172 2.25 0 2.62617 0 3.09375C0 6.34922 1.17773 8.61328 2.75977 10.1496C4.31719 11.6648 6.21563 12.4277 7.61484 12.8145C8.4375 13.043 9 13.7285 9 14.4176C9 15.1523 8.40234 15.75 7.66758 15.75H6.75C6.12773 15.75 5.625 16.2527 5.625 16.875C5.625 17.4973 6.12773 18 6.75 18H13.5C14.1223 18 14.625 17.4973 14.625 16.875C14.625 16.2527 14.1223 15.75 13.5 15.75H12.5824C11.8477 15.75 11.25 15.1523 11.25 14.4176C11.25 13.7285 11.809 13.0395 12.6352 12.8145C14.0379 12.4277 15.9363 11.6648 17.4937 10.1496C19.0723 8.61328 20.25 6.34922 20.25 3.09375C20.25 2.62617 19.8738 2.25 19.4062 2.25H15.6937C15.7043 2.06719 15.7113 1.88437 15.7184 1.69453C15.7535 0.766406 14.9941 0 14.0625 0ZM1.71914 3.9375H4.68633C5.00625 7.10508 5.71289 9.22148 6.51094 10.6383C5.63555 10.2516 4.725 9.70664 3.9375 8.94023C2.8125 7.84688 1.89844 6.26836 1.72266 3.9375H1.71914ZM16.316 8.94023C15.5285 9.70664 14.618 10.2516 13.7426 10.6383C14.5406 9.22148 15.2473 7.10508 15.5672 3.9375H18.5344C18.3551 6.26836 17.441 7.84688 16.3195 8.94023H16.316Z"
                            fill="#FBBF24"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_6362_549">
                            <path d="M0 0H20.25V18H0V0Z" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>

                      <p className="text-black col-md-7 text-center">
                        {t("Total Points")}:{" "}
                        <span
                          className="text-yellow-400"
                          style={{ color: "#FBBF24" }}
                        >
                          {localPoints}
                        </span>
                      </p>
                    </div>
                    <button
                      className="btn-primarysave custom-button"
                      onClick={handleRecordingScoreSave}
                    >
                      {t("Update Score")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <HarinaamBulkUpdate
        isOpen={showBulkUpdate}
        onClose={() => setShowBulkUpdate(false)}
        onSave={handleBulkUpdateSave}
      />
      <HarinaamBulkSchedule
        isOpen={showBulkSchedule}
        onClose={() => setShowBulkSchedule(false)}
        onSave={handleBulkScheduleSave}
      />
    </>
  );
};

export default RecordingOfScores;
